import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { maintenanceApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  CreditCard,
  Users,
  Home,
  Settings,
  LogOut,
  ChevronRight,
  Edit,
  Trash2,
  Menu,
  X,
  Search,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Filter,
  Send,
  Briefcase,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sidebarItems = [
  { icon: Building2, label: 'Dashboard', path: '/admin' },
  { icon: Bell, label: 'Notices', path: '/admin/notices' },
  { icon: FileText, label: 'Documents', path: '/admin/documents' },
  { icon: Wrench, label: 'Maintenance', path: '/admin/maintenance' },
  { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
  { icon: Users, label: 'Residents', path: '/admin/residents' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-700',
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
};

const statusIcons = {
  pending: Clock,
  in_progress: Wrench,
  completed: CheckCircle2,
  cancelled: X,
};

export default function AdminMaintenance() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [formData, setFormData] = useState({
    status: 'pending',
    priority: 'medium',
    assigned_to: '',
    completion_notes: '',
  });
  const [showRequestQuotes, setShowRequestQuotes] = useState(false);
  const [requestingQuotes, setRequestingQuotes] = useState(false);
  const [quoteCategory, setQuoteCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Create ticket state
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    unit_id: '',
  });
  const [units, setUnits] = useState([]);

  const hoaId = user?.hoa_id || 'demo_hoa';
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchTickets();
    fetchCategories();
    fetchUnits();
  }, [hoaId]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contractors/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      // Use fallback categories
      setCategories([
        { id: 'plumber', name: 'Plumber' },
        { id: 'electrician', name: 'Electrician' },
        { id: 'general_maintenance', name: 'General Maintenance' },
      ]);
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/units/${hoaId}`, { credentials: 'include' });
      if (res.ok) {
        setUnits(await res.json());
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title || !newTicket.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    setCreatingTicket(true);
    try {
      const res = await fetch(`${API_URL}/api/maintenance/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hoa_id: hoaId,
          title: newTicket.title,
          description: newTicket.description,
          priority: newTicket.priority,
          unit_id: newTicket.unit_id === 'common_area' ? null : (newTicket.unit_id || null),
          photos: []
        })
      });
      
      if (res.ok) {
        toast.success('Ticket created successfully');
        setShowCreateTicket(false);
        setNewTicket({ title: '', description: '', priority: 'medium', unit_id: '' });
        fetchTickets();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error?.message || 'Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await maintenanceApi.list(hoaId);
      setTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error(error?.message || 'Failed to load maintenance tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    // Require notes when completing or closing a ticket
    if ((formData.status === 'completed' || formData.status === 'closed') && !formData.completion_notes?.trim()) {
      toast.error('Please add notes before closing the ticket');
      return;
    }
    
    try {
      await maintenanceApi.updateStatus(editingTicket.ticket_id, {
        status: formData.status,
        priority: formData.priority,
        assigned_to: formData.assigned_to || null,
        completion_notes: formData.completion_notes || null,
      });
      toast.success('Ticket updated successfully');
      setIsEditOpen(false);
      setEditingTicket(null);
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(error?.message || 'Failed to update ticket');
    }
  };

  const openEditDialog = (ticket) => {
    setEditingTicket(ticket);
    setFormData({
      status: ticket.status,
      priority: ticket.priority,
      assigned_to: ticket.assigned_to || '',
      completion_notes: ticket.completion_notes || '',
    });
    // Auto-select category based on ticket category
    setQuoteCategory(ticket.category || '');
    setIsEditOpen(true);
  };

  const handleRequestQuotes = async () => {
    if (!quoteCategory) {
      toast.error('Please select a category');
      return;
    }

    setRequestingQuotes(true);
    try {
      const res = await fetch(`${API_URL}/api/contractors/jobs/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          ticket_id: editingTicket.ticket_id,
          title: editingTicket.title,
          description: editingTicket.description,
          category: quoteCategory,
          unit_number: editingTicket.unit_id || '',
          location_details: editingTicket.location || '',
        })
      });

      if (res.ok) {
        const data = await res.json();
        const notifiedCount = data.contractors_notified || 0;
        toast.success(
          notifiedCount > 0 
            ? `Job posted! ${notifiedCount} contractor${notifiedCount > 1 ? 's' : ''} notified via email.`
            : 'Job posted! No matching contractors to notify yet.',
          {
            action: {
              label: 'View Jobs',
              onClick: () => navigate('/admin/contractors')
            }
          }
        );
        setShowRequestQuotes(false);
        setIsEditOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create job');
      }
    } catch (error) {
      // Demo fallback
      toast.success('Job posted! 3 contractors notified (Demo)', {
        action: {
          label: 'View Jobs',
          onClick: () => navigate('/admin/contractors')
        }
      });
      setShowRequestQuotes(false);
      setIsEditOpen(false);
    } finally {
      setRequestingQuotes(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tickets.length,
    pending: tickets.filter(t => t.status === 'pending').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    completed: tickets.filter(t => t.status === 'completed').length,
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white">BodyCorp365</p>
                <p className="text-xs text-slate-400">Admin Portal</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      item.path === '/admin/maintenance'
                        ? 'bg-sky-500 text-white'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold text-slate-900">Maintenance</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Maintenance Tickets</h1>
              <p className="text-slate-500 mt-1">Manage and track maintenance requests</p>
            </div>
            <Button 
              onClick={() => setShowCreateTicket(true)}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="log-ticket-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Log Ticket
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('pending')}>
              <CardContent className="p-4">
                <p className="text-sm text-amber-600">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('in_progress')}>
              <CardContent className="p-4">
                <p className="text-sm text-sky-600">In Progress</p>
                <p className="text-2xl font-bold text-sky-600">{stats.in_progress}</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('completed')}>
              <CardContent className="p-4">
                <p className="text-sm text-emerald-600">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              </CardContent>
            </Card>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-tickets-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tickets List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredTickets.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No tickets found</h3>
                <p className="text-slate-500">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filter' 
                    : 'No maintenance requests have been submitted yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredTickets.map((ticket) => {
                const StatusIcon = statusIcons[ticket.status] || Clock;
                return (
                  <Card 
                    key={ticket.ticket_id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openEditDialog(ticket)}
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{ticket.title}</h3>
                            <Badge className={statusColors[ticket.status]}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                            <Badge className={priorityColors[ticket.priority]}>
                              {ticket.priority}
                            </Badge>
                            {ticket.category && (
                              <Badge variant="outline">{ticket.category}</Badge>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm mb-3 line-clamp-2">{ticket.description}</p>
                          {ticket.photos && ticket.photos.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3" data-testid={`ticket-photos-${ticket.ticket_id}`}>
                              {ticket.photos.slice(0, 5).map((photo, idx) => (
                                <a
                                  key={idx}
                                  href={photo}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="block"
                                  data-testid={`ticket-photo-${ticket.ticket_id}-${idx}`}
                                >
                                  <img
                                    src={photo}
                                    alt={`Attachment ${idx + 1}`}
                                    className="w-16 h-16 object-cover rounded-md border border-slate-200 hover:opacity-80"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-xs text-slate-400">
                            <span>Unit: {ticket.unit_id || 'N/A'}</span>
                            <span>•</span>
                            <span>
                              {new Date(ticket.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {ticket.assigned_to && (
                              <>
                                <span>•</span>
                                <span>Assigned to: {ticket.assigned_to}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" data-testid={`edit-ticket-${ticket.ticket_id}`}>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Ticket</DialogTitle>
            <DialogDescription>
              {editingTicket?.title}
            </DialogDescription>
          </DialogHeader>
          
          {!showRequestQuotes ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="edit-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Input
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  placeholder="Enter assignee name"
                  data-testid="edit-assigned-input"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Notes 
                  {(formData.status === 'completed' || formData.status === 'closed') && (
                    <span className="text-red-500 ml-1">* (Required to close)</span>
                  )}
                </Label>
                <Textarea
                  value={formData.completion_notes}
                  onChange={(e) => setFormData({ ...formData, completion_notes: e.target.value })}
                  placeholder="Add notes about this ticket..."
                  rows={3}
                  data-testid="edit-notes-input"
                  className={(formData.status === 'completed' || formData.status === 'closed') && !formData.completion_notes?.trim() ? 'border-red-300' : ''}
                />
              </div>
              
              {/* Request Quotes Section */}
              {editingTicket?.status !== 'completed' && (
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                    onClick={() => setShowRequestQuotes(true)}
                    data-testid="request-quotes-btn"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Request Quotes from Contractors
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Post this job to get quotes from approved contractors
                  </p>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateStatus} className="bg-sky-500 hover:bg-sky-600" data-testid="save-ticket-btn">
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-semibold text-indigo-900">Post to Contractor Marketplace</h4>
                </div>
                <p className="text-sm text-indigo-700">
                  This will create a job listing where approved contractors can submit quotes.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Service Category *</Label>
                <Select value={quoteCategory} onValueChange={setQuoteCategory}>
                  <SelectTrigger data-testid="quote-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-900 mb-1">Job Details:</p>
                <p className="text-slate-600"><strong>Title:</strong> {editingTicket?.title}</p>
                <p className="text-slate-600"><strong>Unit:</strong> {editingTicket?.unit_id || 'Common Area'}</p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequestQuotes(false)}>Back</Button>
                <Button 
                  onClick={handleRequestQuotes} 
                  disabled={requestingQuotes || !quoteCategory}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="submit-job-btn"
                >
                  {requestingQuotes ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Post Job & Notify Contractors
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-sky-500" />
              Log New Maintenance Ticket
            </DialogTitle>
            <DialogDescription>
              Submit a maintenance request for this building
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                placeholder="Brief description of the issue"
                data-testid="new-ticket-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Detailed description of the maintenance issue..."
                rows={4}
                data-testid="new-ticket-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newTicket.priority} onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}>
                  <SelectTrigger data-testid="new-ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low (P3)</SelectItem>
                    <SelectItem value="medium">Medium (P2)</SelectItem>
                    <SelectItem value="high">High (P1 - Critical)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Unit (Optional)</Label>
                <Select value={newTicket.unit_id} onValueChange={(value) => setNewTicket({ ...newTicket, unit_id: value })}>
                  <SelectTrigger data-testid="new-ticket-unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="common_area">Common Area</SelectItem>
                    {units.map(unit => (
                      <SelectItem key={unit.unit_id} value={unit.unit_id}>
                        Unit {unit.unit_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-sky-50 border border-sky-100 rounded-lg">
              <p className="text-sm text-sky-700">
                The resident and trustees will receive an acknowledgement email once this ticket is submitted.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateTicket} 
              className="bg-sky-500 hover:bg-sky-600"
              disabled={creatingTicket || !newTicket.title || !newTicket.description}
              data-testid="submit-new-ticket-btn"
            >
              {creatingTicket ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Ticket
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
